// The selection's handles under the pointer (docs/claude/selection.md, *The
// selection's handles under the pointer*): which of the selection's handles a
// press there works — a knob, a resize handle, a grip, a door's jamb or flip
// icon, a polygon's edge — asked ONCE by the press, the Shift gate, the cursor
// and the hover. It was answered three times, in three orders, and they
// disagreed. The hit tests need the view and stay with main.ts (`handleAt` and
// its probes, one per kind); what lives here is DOM-free:
//  - `HANDLE_ORDER`: every kind, in the order the press tries them — the kind
//    union is read off it, so a new kind is placed in the order or is not one;
//  - `firstHandle`: the first probe that hits, in that order;
//  - `HANDLE_CURSOR`: the pointer each kind shows (`cursorOf`);
//  - `HANDLE_SHIFT`: what Shift means on each (`handleTakesPress`, the gate the
//    group click and the Shift toggle give way to).
// Both tables are Records over the kind: a new kind does not compile until each
// says what it does.
import type {
  BackgroundImage,
  Flooring,
  Polygon,
  ProductInstance,
  RevisionCloud,
  SaunaDoor,
  SectionLine,
  TextComment,
  Vec2,
  Wall,
} from '../shared/types';
import type { HeaterSide } from './geometry';
import type { BenchHandleKind } from './main';
import type {
  Handle,
  SchedTableKind,
} from './render';
import type { SelGrip } from './ui/grips';
import type { HandleInSection } from './ui/handleWork';

/** Every kind of handle the selection can show, in the order the canvas
 *  mouse-down tries them: where two lie under the pointer, the first listed is
 *  the one a press works, the cursor names and the hover yields to. */
export const HANDLE_ORDER = [
  'viewHandle',
  'grip',
  'ceilingArrow',
  'underlayKnob',
  'underlayResize',
  'benchHandle',
  'doorJamb',
  'doorFlip',
  'instKnob',
  'shapeKnob',
  'instResize',
  'nodeHandle',
  'heaterCover',
  'polyEdge',
  'shapeResize',
  'cloudKnob',
  'cloudResize',
  'tableResize',
  'textKnob',
  'textResize',
  'textLeader',
  'sectionEnd',
  'sectionKnob',
  'drainKnob',
] as const;
export type HandleKind = (typeof HANDLE_ORDER)[number];

/** The kinds the press takes before anything else a Select press can mean — a
 *  connection's tag, a view's title, the group click, the Shift toggle. The
 *  others come after those (a tag or a title over one of them takes the press). */
export const TAKEN_FIRST: ReadonlySet<HandleKind> = new Set<HandleKind>(['viewHandle', 'grip', 'ceilingArrow']);

/** Where a resize handle stands and the middle of what it resizes, on screen:
 *  its cursor's double arrow runs from one to the other. */
export interface Aim { at: Vec2; centre: Vec2 }

/** What a hit of each kind carries: the object it belongs to, and what its
 *  press needs. */
export interface HandlePayload {
  /** A height handle in a section view (handles.ts, ui/handleWork.ts). */
  viewHandle: { view: HandleInSection };
  /** A selection grip (ui/grips.ts `selectionGrips`), with its place in the list. */
  grip: { grip: SelGrip & { index: number } };
  /** The single selected room's ceiling fall arrow. */
  ceilingArrow: { room: ProductInstance };
  /** The rotate knob of the underlay being moved / scaled / turned. */
  underlayKnob: { bg: BackgroundImage };
  underlayResize: Aim & { bg: BackgroundImage; handle: Handle };
  /** A border or corner of the selected bench (Select or the Bench tool). */
  benchHandle: { bench: BenchHandleKind };
  /** A jamb of the selected room's selected door ('left' = its run's start). */
  doorJamb: Aim & { side: 'left' | 'right' };
  /** The selected door's flip icons: its hinge, or (in a partition) the side
   *  its leaf swings to. */
  doorFlip: { flip: 'hinge' | 'swing'; inst: ProductInstance; door: SaunaDoor };
  instKnob: { inst: ProductInstance };
  shapeKnob: { shape: Polygon | Wall };
  /** A resize handle of the selected part or room (a mid-edge one over its own
   *  door, pane or opening gives way to it, so it is no hit there). */
  instResize: Aim & { inst: ProductInstance; handle: Handle };
  /** A node of the selected node object (a polyline bench, a salt wall). */
  nodeHandle: { inst: ProductInstance; handle: Handle };
  /** A guard-cover side of the selected heater: built (`add`) or cleared. */
  heaterCover: { cover: { instId: string; side: HeaterSide; kind: 'add' | 'remove' } };
  /** An edge of the lone selected polygon, its ends left to the corner handles:
   *  a click selects it — a rectangle's mid-edge resize handle lies on it and
   *  gives way. */
  polyEdge: { poly: Polygon; edge: number };
  shapeResize: Aim & { poly: Polygon; handle: Handle };
  cloudKnob: { cloud: RevisionCloud };
  cloudResize: Aim & { cloud: RevisionCloud; handle: Handle };
  /** The selected schedule table's width handle (-1 its left edge, 1 its right). */
  tableResize: { table: SchedTableKind; side: -1 | 1 };
  textKnob: { text: TextComment };
  textResize: Aim & { text: TextComment; handle: Handle };
  /** The tip of the selected text box's leader arrow. */
  textLeader: { text: TextComment };
  /** An end of the selected section's cut line. */
  sectionEnd: { sec: SectionLine; end: 'a' | 'b' };
  /** The selected section's rotate handle (it turns the view drawn). */
  sectionKnob: { sec: SectionLine };
  /** The rotate knob of the selected floor's drain. */
  drainKnob: { floor: Flooring };
}

/** The selection's handle under the pointer: a tagged union over every kind. */
export type HandleHit = { [K in HandleKind]: { kind: K } & HandlePayload[K] }[HandleKind];
/** A hit of one kind. */
export type HitOf<K extends HandleKind> = Extract<HandleHit, { kind: K }>;

/** One hit test per kind: its payload when the pointer is on it, else null. */
export type HandleProbes<A extends unknown[]> = { [K in HandleKind]: (...args: A) => HandlePayload[K] | null };

/** The first kind, in `HANDLE_ORDER`, whose probe hits — later probes are not
 *  asked —, or null. */
export function firstHandle<A extends unknown[]>(probes: HandleProbes<A>, ...args: A): HandleHit | null {
  for (const kind of HANDLE_ORDER) {
    const p = probes[kind](...args);
    if (p) return { ...p, kind } as HandleHit;
  }
  return null;
}

/** The hit when it is of `kind`, else null — what each kind's press takes. */
export function handleOf<K extends HandleKind>(hit: HandleHit | null, kind: K): HitOf<K> | null {
  return hit && hit.kind === kind ? (hit as HitOf<K>) : null;
}

/** The double-arrow resize cursor for a handle at `hs`, matched to the direction
 *  from its object's on-screen centre `cs` — so it names the edge the handle moves
 *  whatever the object's rotation. Screen px (y down). */
export function dirResizeCursor(hs: Vec2, cs: Vec2): string {
  const deg = ((Math.atan2(hs.y - cs.y, hs.x - cs.x) * 180) / Math.PI + 360) % 180;
  if (deg < 22.5 || deg >= 157.5) return 'ew-resize';
  if (deg < 67.5) return 'nwse-resize'; // ↘↖ (screen y is down)
  if (deg < 112.5) return 'ns-resize';
  return 'nesw-resize'; // ↗↙
}

const resize = (h: Aim): string => dirResizeCursor(h.at, h.centre);

/** The pointer each kind shows in Select: the action a press there starts —
 *  grab over a knob, a matched double arrow over a resize handle, move over what
 *  drags to a point, a hand over what a click acts on. */
export const HANDLE_CURSOR: { [K in HandleKind]: (hit: HitOf<K>) => string } = {
  viewHandle: () => 'move',
  grip: () => 'move',
  ceilingArrow: () => 'grab',
  underlayKnob: () => 'grab',
  underlayResize: resize,
  benchHandle: () => 'move',
  doorJamb: resize,
  doorFlip: () => 'pointer',
  instKnob: () => 'grab',
  shapeKnob: () => 'grab',
  instResize: resize,
  nodeHandle: () => 'move',
  heaterCover: () => 'pointer',
  polyEdge: () => 'pointer',
  shapeResize: resize,
  cloudKnob: () => 'grab',
  cloudResize: resize,
  tableResize: () => 'ew-resize',
  textKnob: () => 'grab',
  textResize: resize,
  textLeader: () => 'move',
  sectionEnd: () => 'move',
  sectionKnob: () => 'grab',
  drainKnob: () => 'grab',
};

/** The pointer over a hit (`HANDLE_CURSOR`). */
export function cursorOf(hit: HandleHit): string {
  return (HANDLE_CURSOR[hit.kind] as (h: HandleHit) => string)(hit);
}

/** What Shift means on a press of a kind (docs/claude/tools.md, *Modifiers*):
 *  - `steps`: it turns — or its run from its neighbour is angled — in 15° steps;
 *  - `proportions`: it resizes keeping its proportions (Link W & H);
 *  - `none`: nothing, but the press is still the handle's drag;
 *  - `select`: it is a click target, not a drag — Shift+click there keeps its
 *    selection meaning (the group click and the Shift toggle come first). */
export type ShiftMeaning = 'none' | 'proportions' | 'select' | 'steps';
export const HANDLE_SHIFT: Record<HandleKind, ShiftMeaning> = {
  viewHandle: 'none',
  grip: 'steps',
  ceilingArrow: 'none',
  underlayKnob: 'steps',
  underlayResize: 'proportions',
  benchHandle: 'none',
  doorJamb: 'none',
  doorFlip: 'select',
  instKnob: 'steps',
  shapeKnob: 'steps',
  instResize: 'proportions',
  nodeHandle: 'steps',
  heaterCover: 'select',
  polyEdge: 'select',
  shapeResize: 'proportions',
  cloudKnob: 'steps',
  cloudResize: 'proportions',
  tableResize: 'none',
  textKnob: 'steps',
  textResize: 'none',
  textLeader: 'none',
  sectionEnd: 'steps',
  sectionKnob: 'steps',
  drainKnob: 'steps',
};

/** Whether the press is the handle's: a drag of it, Shift or not, so the group
 *  click and the Shift toggle give way (a Shift-press on a knob turns it in 15°
 *  steps, on a resize handle keeps proportions). A click target (`select`) is
 *  not: Shift+click there still toggles the selection. */
export function handleTakesPress(hit: HandleHit | null): boolean {
  return !!hit && HANDLE_SHIFT[hit.kind] !== 'select';
}
